import { NavLink, useParams } from "react-router";
import { useState } from "react";
import t from "@/lang/lang";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { H3 } from "@/shared/components/typography";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import { ListBuildingRequest } from "@/services/backend/buildings/api/building-api";
import { BuildingsTable } from "@/services/backend/buildings/components/buildings-table";
import { AddBuildingComponent } from "@/services/backend/buildings/components/add-building-component";
import { Button } from "@/shared/components/ui/button";
import { BuildingWithoutAreas } from "@/services/backend/buildings/types/building";
import {
  addressListLabel,
  isEmptyAddress,
} from "@/services/backend/addresses/types/address";

export function BuildingsRoute() {
  const { customerId } = useParams();
  const [request, setRequest] = useState<ListBuildingRequest>({
    customer: { active: true, values: [customerId ?? NullUUID] },
    orderBy: [{ field: "name" }],
    limit: 10,
    offset: 0,
  });

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/buildings`,
              label: t("Gebäudeverwaltung"),
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent>
        <H3>{t("Gebäudeverwaltung")}</H3>
        <div className="text-muted-foreground text-sm">
          {t("Hier werden die Gebäude eines Kunden verwaltet.")}
        </div>
        <Button variant="link" className="h-auto self-start px-0 py-0" asChild>
          <NavLink to={`/customers/${customerId}/buildings/addresses`}>
            {t("Alle Adressen")}
          </NavLink>
        </Button>
        <div className="mt-4 grid gap-2">
          <BuildingsTable
            request={request}
            onRequestChange={setRequest}
            renderBuildingLink={(building) => (
              <BuildingNameCell building={building} />
            )}
            renderAddressLink={(building) => (
              <AddressCell building={building} />
            )}
            createAction={
              customerId ? (
                <AddBuildingComponent customerId={customerId} />
              ) : null
            }
          />
        </div>
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function BuildingNameCell({ building }: { building: BuildingWithoutAreas }) {
  const { customerId } = useParams();

  return (
    <Button variant="link" className="h-auto px-0 py-0" asChild>
      <NavLink to={`/customers/${customerId}/buildings/${building.id}`}>
        {building.name}
      </NavLink>
    </Button>
  );
}

function AddressCell({ building }: { building: BuildingWithoutAreas }) {
  const { customerId } = useParams();
  if (isEmptyAddress(building.address)) {
    return <span className="text-muted-foreground">{t("Keine Adresse")}</span>;
  }

  return (
    <Button variant="link" className="h-auto px-0 py-0" asChild>
      <NavLink
        to={`/customers/${customerId}/buildings/addresses/${building.addressKey}`}
      >
        {addressListLabel(building.address)}
      </NavLink>
    </Button>
  );
}
